/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Button } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { ICwsBuyNowData } from './cws-buy-now.data';
import { ICwsBuyNowProps } from './cws-buy-now.props.autogenerated';

export interface ICwsBuyNowViewProps extends ICwsBuyNowProps<ICwsBuyNowData> {
    buyNowButton: React.ReactNode | null;
}

/**
 *
 * CwsBuyNow component
 * @extends {React.PureComponent<ICwsBuyNowProps<ICwsBuyNowData>>}
 */
class CwsBuyNow extends React.PureComponent<ICwsBuyNowProps<ICwsBuyNowData>> {
    private readonly _getProductSku = (): string | undefined => {
        const product = this.props.data?.product?.result as { ItemId?: string; productVariant?: { ItemId?: string } } | undefined;

        return product?.ItemId || product?.productVariant?.ItemId;
    };

    private readonly _appendProductSkuToUrl = (baseUrl: string, sku: string): string => {
        const hashIndex = baseUrl.indexOf('#');
        const hash = hashIndex >= 0 ? baseUrl.substring(hashIndex) : '';
        const urlWithoutHash = hashIndex >= 0 ? baseUrl.substring(0, hashIndex) : baseUrl;
        const separator = urlWithoutHash.includes('?') ? '&' : '?';

        return `${urlWithoutHash}${separator}product=${encodeURIComponent(sku)}${hash}`;
    };

    private readonly _getBuyNowUrl = (): string | undefined => {
        const destinationUrl = this.props.config.link?.linkUrl?.destinationUrl;
        const sku = this._getProductSku();

        if (!destinationUrl) {
            return undefined;
        }

        if (!sku) {
            return destinationUrl;
        }

        return this._appendProductSkuToUrl(destinationUrl, sku);
    };

    public render(): JSX.Element | null {
        const linkConfig = this.props.config.link;
        const href = this._getBuyNowUrl();
        const linkText = linkConfig?.linkText;

        return this.props.renderView({
            ...this.props,
            buyNowButton:
                linkText && href ? (
                    <Button
                        className='msc-cta__primary cws-buy-now__button'
                        title={linkConfig?.ariaLabel || linkText}
                        aria-label={linkConfig?.ariaLabel}
                        href={href}
                        target={linkConfig?.openInNewTab ? '_blank' : undefined}
                    >
                        {linkText}
                    </Button>
                ) : null
        });
    }
}

export default CwsBuyNow;
